// ====================================================
// src/components/block/liveStreamManagement/videoEffects/backgroundImageStore.ts
//
// Petite bibliothèque d'images de fond pour l'effet "Arrière-plan" — INDÉPENDANTE
// des 12 slots "Images" du deck (ImageSlot, cf. MediaStreamContext.tsx) : ceux-là
// portent une sémantique de positionnement d'incrustation (x/y/width/height/opacity)
// qui n'a rien à voir ici. On réutilise en revanche le même mécanisme de stockage bas
// niveau (local_media_store.ts, IndexedDB + blob: URL) — c'est la seule brique déjà
// éprouvée dans le projet pour "l'utilisateur choisit un fichier, on le garde".
//
// Portée : globale à ce navigateur/poste, comme les slots d'images eux-mêmes (pas de
// notion de chaîne ou de compte créateur ici).
// ====================================================

import { storeMediaDataUrl, restoreMediaUrl, deleteMediaRecord } from '../../../../backendServices/liveStreamManagment/local_media_store';

const STORAGE_KEY = 'kleon_regie_background_images';

export interface BackgroundImage {
  id: string;
  label: string;
  mediaId: string;
  url: string;
}

interface StoredEntry { id: string; label: string; mediaId: string; }

function loadEntries(): StoredEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveEntries(entries: StoredEntry[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries)); } catch { /* best-effort */ }
}

let cache: BackgroundImage[] | null = null;
let loadingPromise: Promise<BackgroundImage[]> | null = null;
const listeners = new Set<() => void>();

export function subscribeBackgroundImages(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function notify(): void {
  listeners.forEach(fn => fn());
}

/**
 * Résout les blob: URL depuis IndexedDB (elles ne survivent pas à un rechargement de
 * page). Les entrées dont le blob a disparu (nettoyage navigateur, autre machine) sont
 * silencieusement retirées de la liste persistée — mieux vaut une bibliothèque propre
 * qu'une miniature cassée.
 */
export function loadBackgroundImages(): Promise<BackgroundImage[]> {
  if (cache) return Promise.resolve(cache);
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const entries  = loadEntries();
    const resolved = await Promise.all(entries.map(async e => {
      const url = await restoreMediaUrl(e.mediaId);
      return url ? { ...e, url } : null;
    }));
    const images = resolved.filter((x): x is BackgroundImage => !!x);
    if (images.length !== entries.length) {
      saveEntries(images.map(({ id, label, mediaId }) => ({ id, label, mediaId })));
    }
    cache = images;
    return images;
  })();

  return loadingPromise;
}

export function getCachedBackgroundImages(): BackgroundImage[] {
  return cache ?? [];
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export async function addBackgroundImage(file: File): Promise<BackgroundImage> {
  const dataUrl = await fileToDataUrl(file);
  const id      = `bg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const { mediaId, objectUrl } = await storeMediaDataUrl(dataUrl, file.name, 'image', id, 'background');
  const label = file.name.replace(/\.[^.]+$/, '').slice(0, 24) || 'Fond';
  const image: BackgroundImage = { id, label, mediaId, url: objectUrl };

  const entries = loadEntries();
  entries.push({ id, label, mediaId });
  saveEntries(entries);

  cache = [...(cache ?? []), image];
  notify();
  return image;
}

export async function removeBackgroundImage(id: string): Promise<void> {
  const entries = loadEntries();
  const entry   = entries.find(e => e.id === id);
  saveEntries(entries.filter(e => e.id !== id));
  if (entry) await deleteMediaRecord(entry.mediaId);
  cache = (cache ?? []).filter(img => img.id !== id);
  notify();
}
